import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Users, 
  Clock, 
  CheckCircle,
  XCircle,
  Calendar,
  Loader2,
  Eye,
  MessageSquare,
  User
} from 'lucide-react';
import { getEmployerApplications, updateApplicationStatus, getEmployerJobs } from '../../services/api';
import { toast } from 'sonner';

const EmployerApplications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedJob]);

  const fetchData = async () => {
    try {
      const [appsRes, jobsRes] = await Promise.all([
        getEmployerApplications(selectedJob === 'all' ? undefined : selectedJob),
        getEmployerJobs()
      ]);
      setApplications(appsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await updateApplicationStatus(applicationId, newStatus);
      setApplications(applications.map(app => 
        app.application_id === applicationId 
          ? { ...app, status: newStatus }
          : app
      ));
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ausstehend', color: 'bg-gray-100 text-gray-700' },
      reviewing: { label: 'In Prüfung', color: 'bg-blue-100 text-blue-700' },
      interview: { label: 'Interview', color: 'bg-green-100 text-green-700' },
      accepted: { label: 'Angenommen', color: 'bg-green-100 text-green-700' },
      rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' }
    };
    const config = statusMap[status] || statusMap.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="employer-applications-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Bewerbungen</h1>
            <p className="text-muted-foreground mt-1">
              {applications.length} Bewerbungen
            </p>
          </div>
          <Select value={selectedJob} onValueChange={setSelectedJob}>
            <SelectTrigger className="w-64" data-testid="job-filter-select">
              <SelectValue placeholder="Alle Jobs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Jobs</SelectItem>
              {jobs.map(job => (
                <SelectItem key={job.job_id} value={job.job_id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Applications List */}
        {applications.length > 0 ? (
          <div className="grid gap-4">
            {applications.map((app) => (
              <Card 
                key={app.application_id} 
                className="hover:shadow-md transition-all"
                data-testid={`employer-application-${app.application_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{app.applicant_name || 'Bewerber'}</h3>
                        <p className="text-sm text-muted-foreground">
                          für: {app.job_title || 'Position'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(app.created_at).toLocaleDateString('de-DE')}
                        </p>
                        
                        {app.applicant_skills && app.applicant_skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {app.applicant_skills.slice(0, 5).map((skill, idx) => (
                              <span 
                                key={idx}
                                className="px-2 py-0.5 bg-secondary text-secondary-foreground rounded text-xs"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(app.status)}
                      <Select 
                        value={app.status} 
                        onValueChange={(value) => handleStatusChange(app.application_id, value)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Ausstehend</SelectItem>
                          <SelectItem value="reviewing">In Prüfung</SelectItem>
                          <SelectItem value="interview">Interview</SelectItem>
                          <SelectItem value="accepted">Angenommen</SelectItem>
                          <SelectItem value="rejected">Abgelehnt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {app.cover_letter && (
                    <div className="mt-4 p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {app.cover_letter}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Keine Bewerbungen</h3>
              <p className="text-muted-foreground">
                Es liegen noch keine Bewerbungen vor
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default EmployerApplications;
